import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { adminService } from '../../services';
import { LoadingSpinner } from '../../components/ui/Loading';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import CreateQuestModal from '../../components/modals/CreateQuestModal';
import { isAdminEquivalent } from '../../utils/permissions';

const AdminQuests = () => {
  const { user } = useAuth();
  const { notify } = useNotification();
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, questId: null });
  const itemsPerPage = 12;

  const loadQuests = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm.trim() || undefined,
        type: typeFilter || undefined,
        difficulty: difficultyFilter || undefined,
      };

      const response = await adminService.getQuests(params);
      setQuests(response.data?.quests || []);
      setTotalPages(response.data?.totalPages || 1);
      setTotalItems(response.data?.total || 0);
    } catch (error) {
      console.error('Erro ao carregar quests:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, typeFilter, difficultyFilter]);

  useEffect(() => {
    if (isAdminEquivalent(user?.role)) {
      loadQuests();
    }
  }, [user, loadQuests]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDifficultyFilterChange = (e) => {
    setDifficultyFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateQuest = async (questData) => {
    try {
      await adminService.createQuest(questData);
      await loadQuests(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao criar quest:', error);
      throw error;
    }
  };

  const handleEditQuest = async (quest) => {
    // TODO: Implementar modal de edição de quest
    console.log('Editar quest:', quest);
  };

  const handleDeleteQuest = async (questId) => {
    setConfirmModal({ isOpen: true, questId });
  };

  const confirmDelete = async () => {
    const questId = confirmModal.questId;
    setConfirmModal({ isOpen: false, questId: null });

    try {
      await adminService.deleteQuest(questId);
      notify.success('Quest excluída com sucesso!', '🗑️ Quest Removida');
      await loadQuests();
    } catch (error) {
      console.error('Erro ao excluir quest:', error);
      notify.error('Erro ao excluir quest. Tente novamente.', 'Erro');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400 bg-green-500/20 border border-green-500/30';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20 border border-yellow-500/30';
      case 'hard': return 'text-red-400 bg-red-500/20 border border-red-500/30';
      case 'legendary': return 'text-purple-400 bg-purple-500/20 border border-purple-500/30';
      default: return 'text-gray-400 bg-gray-500/20 border border-gray-500/30';
    }
  };

  const getDifficultyLabel = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'Fácil';
      case 'medium': return 'Médio';
      case 'hard': return 'Difícil';
      case 'legendary': return 'Lendário';
      default: return difficulty;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'main': return '🌟';
      case 'side': return '📝';
      case 'daily': return '📅';
      case 'weekly': return '📊';
      case 'event': return '🎉';
      default: return '📜';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'main': return 'Principal';
      case 'side': return 'Secundária';
      case 'daily': return 'Diária';
      case 'weekly': return 'Semanal';
      case 'event': return 'Evento';
      default: return type;
    }
  };

  if (!isAdminEquivalent(user?.role)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-8">
            <p className="text-red-400">Acesso negado. Apenas administradores podem acessar esta página.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Card */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/app/admin"
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-gray-400" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  📜 Gerenciar Quests
                </h1>
                <p className="mt-1 text-sm text-gray-400">
                  {totalItems > 0 ? `${totalItems} quests encontradas` : 'Gerencie as missões do jogo'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-purple-500/50 font-medium"
            >
              + Nova Quest
            </button>
          </div>
        </div>

        {/* Filters Card */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Buscar Quests
              </label>
              <input
                type="text"
                placeholder="Nome ou descrição..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tipo
              </label>
              <select
                value={typeFilter}
                onChange={handleTypeFilterChange}
                className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              >
                <option value="">Todos os tipos</option>
                <option value="main">Principal</option>
                <option value="side">Secundária</option>
                <option value="daily">Diária</option>
                <option value="weekly">Semanal</option>
                <option value="event">Evento</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Dificuldade
              </label>
              <select
                value={difficultyFilter}
                onChange={handleDifficultyFilterChange}
                className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              >
                <option value="">Todas as dificuldades</option>
                <option value="easy">Fácil</option>
                <option value="medium">Médio</option>
                <option value="hard">Difícil</option>
                <option value="legendary">Lendário</option>
              </select>
            </div>
          </div>
        </div>

        {/* Quests Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {quests.length === 0 ? (
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-12">
                <div className="text-center">
                  <div className="text-6xl mb-4">📜</div>
                  <h3 className="text-lg font-medium text-white mb-2">
                    Nenhuma quest encontrada
                  </h3>
                  <p className="text-gray-400 mb-4">
                    {searchTerm || typeFilter || difficultyFilter ? 'Tente ajustar os filtros de busca.' : 'Comece criando sua primeira quest.'}
                  </p>
                  {!searchTerm && !typeFilter && !difficultyFilter && (
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-purple-500/50 font-medium"
                    >
                      + Criar Primeira Quest
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {quests.map((quest) => (
                  <div
                    key={quest.id}
                    className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-4 hover:border-purple-500/50 hover:shadow-purple-500/20 transition-all duration-300 group"
                  >
                    {/* Header da Quest */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="text-2xl">{getTypeIcon(quest.type)}</div>
                        <div>
                          <h3 className="font-semibold text-white text-sm group-hover:text-purple-300 transition-colors">
                            {quest.name}
                          </h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs text-gray-400">
                              {getTypeLabel(quest.type)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dificuldade */}
                    <div className="mb-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(quest.difficulty)}`}>
                        {getDifficultyLabel(quest.difficulty)}
                      </span>
                    </div>

                    {/* Descrição */}
                    <div className="mb-4">
                      <p className="text-xs text-gray-400 line-clamp-2">
                        {quest.description || 'Sem descrição'}
                      </p>
                    </div>

                    {/* Recompensas */}
                    <div className="mb-4 space-y-1">
                      {quest.experienceReward && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-400">XP:</span>
                          <span className="text-blue-400 font-medium">+{quest.experienceReward}</span>
                        </div>
                      )}
                      {quest.goldReward && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-400">Ouro:</span>
                          <span className="text-yellow-400 font-medium">+{quest.goldReward}</span>
                        </div>
                      )}
                      {quest.itemReward && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-400">Item:</span>
                          <span className="text-green-400 font-medium">{quest.itemReward.name}</span>
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">Status:</span>
                        <span className={`text-xs font-medium ${quest.isActive ? 'text-green-400' : 'text-gray-500'}`}>
                          {quest.isActive ? '✅ Ativa' : '⏸️ Inativa'}
                        </span>
                      </div>
                    </div>

                    {/* Ações */}
                    <div className="flex space-x-2 pt-3 border-t border-white/10">
                      <button
                        onClick={() => handleEditQuest(quest)}
                        className="flex-1 px-3 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white text-xs rounded-lg transition-colors border border-white/10 hover:border-purple-500/50"
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={() => handleDeleteQuest(quest.id)}
                        className="flex-1 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs rounded-lg transition-colors border border-red-500/20 hover:border-red-500/50"
                      >
                        🗑️ Excluir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-300">
                    Página {currentPage} de {totalPages} ({totalItems} quests)
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 disabled:bg-slate-800/50 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors border border-white/10 disabled:border-white/5"
                    >
                      ← Anterior
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 disabled:bg-slate-800/50 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors border border-white/10 disabled:border-white/5"
                    >
                      Próxima →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Modal de Criação */}
        <CreateQuestModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateQuest}
        />

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ isOpen: false, questId: null })}
          onConfirm={confirmDelete}
          title="Excluir Quest"
          message="Tem certeza que deseja excluir esta quest? Esta ação não pode ser desfeita."
          confirmText="Excluir"
          cancelText="Cancelar"
          type="danger"
        />
      </div>
    </div>
  );
};

export default AdminQuests;